/* ⚠️ GENERATED from app/services/commentMentions.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   💬 services/commentMentions.ts — "@someone" in a Vibe comment.

   One rule, three readers: the phone's comment sheet, the browser's thread and
   the server route that writes the comment and tells the people it names.
   Generated into allo-web/src/services and server/functions by
   tools/gen-twins.mjs — edit this file, not the twins.

   🟢 THE SAME ALGORITHM AS A GROUP CHAT'S "@". Which run the caret is in, who
      matches it, what gets inserted, and which "@Ali Hassan" is Ali and which
      is Ali Hassan are all services/mentions — this file only adds what a
      comment needs on top: who the picker can offer when there is no member
      list, and what a comment RECORDS.

   🟥 A COMMENT RECORDS THE NAME AS WRITTEN, which a chat message does not. A
      chat re-derives its blue runs from the live member list, because the room
      IS the list. A comment has no room: the person named may be nobody else on
      the thread, and may rename tomorrow — after which "@Ali" in the text would
      match nothing and quietly stop being a link. So the comment carries
      `mentions: [uid]` (who) and `mentionNames: { uid: name }` (the exact words
      that were inserted), and the blue runs are drawn from those. A comment
      already carries its author's name and picture; one more name of somebody
      the author chose to name publicly discloses nothing new.

   🟥 ONLY WHAT IS STILL IN THE TEXT COUNTS. Pick a name, backspace over it,
      send: the picker's list still holds that uid, the words do not. Every
      record is re-derived from the text, by segmentMentions, on the phone AND
      again on the server — a client cannot make the server notify somebody by
      claiming a mention the words do not contain.
   ═══════════════════════════════════════════════════════════════════════════ */
import { segmentMentions, collectMentions, cleanMentionName, normalizeForMention } from './mentions';
/** at most this many people named in one comment — past it nobody is told */
export const COMMENT_MENTION_MAX = 10;
/** a name longer than this is not a name, it is a paragraph */
export const COMMENT_MENTION_NAME_MAX = 60;
/** the landing highlight on the row a mention points at */
export const MENTION_FLASH_MS = 2000;

/** The notification row's own words — the bell's, on both clients. */
export const MENTION_NOTIF_TEXT = 'mentioned you in a comment';
/** The push, localised by key on the server (notifL10n) and the phone (notifStrings). */
export const MENTION_TITLE_KEY = 'mention_title';
export const MENTION_BODY_KEY = 'comment_mention_body';
export function commentMentionPushBody(actorName) {
  const who = cleanMentionName(actorName) || 'Someone';
  return `${who} ${MENTION_NOTIF_TEXT}`;
}
const _str = v => String(v == null ? '' : v);

/** The people a comment names, as it recorded them. */
export function commentMentionMembers(c) {
  const uids = Array.isArray(c?.mentions) ? c.mentions : [];
  const names = c && c.mentionNames && typeof c.mentionNames === 'object' ? c.mentionNames : {};
  const out = [];
  const seen = new Set();
  for (const raw of uids) {
    const uid = _str(raw).trim();
    if (!uid || seen.has(uid)) continue;
    const name = cleanMentionName(names[uid]);
    if (!name) continue;
    seen.add(uid);
    out.push({
      uid,
      name
    });
  }
  return out;
}

/**
 * What a comment stores about its mentions — derived from the TEXT, against the
 * people the author picked. The author is never recorded (naming yourself tells
 * nobody anything); a run nobody picked is just words.
 * Null when there are none, so a comment without mentions keeps its old shape.
 */
export function mentionsForComment(text, picked, authorUid) {
  const me = _str(authorUid);
  const pool = (Array.isArray(picked) ? picked : []).map(m => ({
    uid: _str(m && m.uid).trim(),
    name: cleanMentionName(m && m.name).slice(0, COMMENT_MENTION_NAME_MAX)
  })).filter(m => m.uid && m.name && m.uid !== me);
  if (!pool.length) return null;
  const uids = collectMentions(_str(text), pool).slice(0, COMMENT_MENTION_MAX);
  if (!uids.length) return null;
  const mentionNames = {};
  for (const uid of uids) {
    const m = pool.find(p => p.uid === uid);
    if (m) mentionNames[uid] = m.name;
  }
  return {
    mentions: uids,
    mentionNames
  };
}

/**
 * The server's reading of what a client CLAIMS: the same derivation, from the
 * claimed uids and names, so nothing the text does not say survives.
 */
export function sanitizeCommentMentions(text, claimed, authorUid) {
  const uids = Array.isArray(claimed?.mentions) ? claimed.mentions.slice(0, COMMENT_MENTION_MAX * 3) : [];
  const names = claimed && claimed.mentionNames && typeof claimed.mentionNames === 'object' ? claimed.mentionNames : {};
  const picked = [];
  for (const u of uids) {
    const uid = _str(u).trim();
    if (!uid || uid.length > 128 || /[\/\s]/.test(uid)) continue; // a Firestore doc id, nothing else
    picked.push({
      uid,
      name: _str(names[uid])
    });
  }
  return mentionsForComment(text, picked, authorUid);
}

/** The comment's words in runs — `uid` set on a run that names somebody. */
export function commentSegments(c) {
  const text = _str(c?.content);
  const members = commentMentionMembers(c);
  if (!members.length) return text ? [{
    text
  }] : [];
  return segmentMentions(text, members);
}

/** Does this comment name `uid`? */
export function commentMentions(c, uid) {
  const u = _str(uid);
  return !!u && commentMentionMembers(c).some(m => m.uid === u);
}

/**
 * Who is told about the mentions in a comment: the people it names, less its
 * author and anyone the comment is already notifying for another reason (the
 * post's owner hears "commented", the person answered hears "replied" — a
 * second buzz for the same words is how people turn notifications off).
 */
export function mentionRecipients(c, authorUid, alreadyTold) {
  const me = _str(authorUid);
  const told = new Set((Array.isArray(alreadyTold) ? alreadyTold : []).map(_str));
  return commentMentionMembers(c).map(m => m.uid).filter(u => u && u !== me && !told.has(u));
}

/**
 * The picker's candidates when there is no member list: everybody already in
 * the conversation (the post's author, everyone who commented, anyone a
 * comment named) followed by whoever a search found — one row per person, the
 * author of the comment being written left out, the names the people in the
 * thread are known by preferred over a search hit's.
 */
export function mentionCandidates(opts) {
  const me = _str(opts?.myUid);
  const names = opts?.names || {};
  const photos = opts?.photos || {};
  const out = [];
  const seen = new Set();
  const add = (uid, name, photo, username) => {
    const u = _str(uid).trim();
    if (!u || u === me || seen.has(u)) return;
    const n = cleanMentionName(names[u]) || cleanMentionName(name) || cleanMentionName(username);
    if (!n) return;
    seen.add(u);
    out.push({
      uid: u,
      name: n.slice(0, COMMENT_MENTION_NAME_MAX),
      photo: photos[u] || photo || null,
      username: username ? _str(username) : null
    });
  };
  const p = opts?.post;
  /* a page's post is signed by the page — the person behind it is not who the
     thread knows, so the post's author is offered only when it is a person */
  if (p && p.userId && !p.pageId) add(p.userId, p.userName, p.userImage);
  for (const c of Array.isArray(opts?.comments) ? opts.comments : []) {
    if (!c) continue;
    if (!c.pageId) add(c.userId, c.userName, c.userImage);
    for (const m of commentMentionMembers(c)) add(m.uid, m.name);
  }
  for (const m of Array.isArray(opts?.extra) ? opts.extra : []) add(m && m.uid, m && m.name, m && m.photo, m && m.username);
  return out;
}

/** Does `query` look like a name worth asking the server about? */
export function mentionSearchTerm(query) {
  const q = normalizeForMention(query);
  return q.length >= 1 && q.length <= 40 ? q : '';
}
